import React, { useState, useEffect } from 'react';
import { doc, getDoc, collectionGroup, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { getAuth, deleteUser } from 'firebase/auth';
import { db } from '../../config/firebase';
import { syncFcmToken } from '../../config/messaging';
import { Clock, CheckCircle, MapPin, Calendar, IndianRupee, Phone, Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header';
import NotificationPrompt from '../common/NotificationPrompt';
import InstallPwaPrompt from '../common/InstallPwaPrompt';

const BRAND_COLORS = {
  primary: '#1a1a1a',
  accent: '#C9A961',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
};

const ProfessionalHomePage = ({ user, userProfile }) => {
  const [professionalData, setProfessionalData] = useState(null);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [invitedJobs, setInvitedJobs] = useState([]);
  const [workHistory, setWorkHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('home');
  const navigate = useNavigate();
  const [showVerifiedBanner, setShowVerifiedBanner] = useState(true);

  // Notification Sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  useEffect(() => {
    const fetchProfessionalData = async () => {
      try {
        const profDoc = await getDoc(doc(db, 'professionals', user.uid));
        if (profDoc.exists()) {
          setProfessionalData(profDoc.data());
        } else if (userProfile) {
          console.log("Using userProfile fallback");
          setProfessionalData(userProfile);
        }

        // Fetch Applications
        const appsRef = collectionGroup(db, 'applications');

        // Note: Accepted and Rejected jobs are now handled via real-time listeners below
      } catch (error) {
        console.error('Error fetching professional data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProfessionalData();
      // Sync FCM token silently
      syncFcmToken(user.uid, 'professionals');

      // Check if verified banner was dismissed
      const dismissed = localStorage.getItem(`skillvend_professional_verified_banner_dismissed_${user.uid}`);
      if (dismissed === 'true') {
        setShowVerifiedBanner(false);
      }

      // Real-time listener for Invited Applications (Job Invites)
      const appsRef = collectionGroup(db, 'applications');
      const invitedQuery = query(
        appsRef,
        where('professionalId', '==', user.uid),
        where('status', '==', 'invited'),
        orderBy('updatedAt', 'desc')
      );

      let isFirstLoad = true;

      const unsubscribeInvited = onSnapshot(invitedQuery, (snapshot) => {
        const invited = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Play sound if we have invited jobs and the count increased (or it's the first load and we have invited)
        if (!isFirstLoad && snapshot.docChanges().some(change => change.type === 'added')) {
          playNotificationSound();
        }

        isFirstLoad = false;

        setInvitedJobs(invited);
      });

      // Real-time listener for Accepted Applications (Upcoming Jobs)
      const acceptedQuery = query(
        appsRef,
        where('professionalId', '==', user.uid),
        where('status', '==', 'accepted'),
        orderBy('updatedAt', 'desc')
      );

      const unsubscribeAccepted = onSnapshot(acceptedQuery, (snapshot) => {
        const accepted = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUpcomingJobs(accepted);
      });

      // Real-time listener for Completed Applications (Work History)
      const completedQuery = query(
        appsRef,
        where('professionalId', '==', user.uid),
        where('status', '==', 'completed'),
        orderBy('updatedAt', 'desc')
      );

      const unsubscribeCompleted = onSnapshot(completedQuery, (snapshot) => {
        const completed = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setWorkHistory(completed);
      });

      return () => {
        unsubscribeInvited();
        unsubscribeAccepted();
        unsubscribeCompleted();
      };
    }
  }, [user]);

  const handleDismissVerifiedBanner = () => {
    setShowVerifiedBanner(false);
    localStorage.setItem(`skillvend_professional_verified_banner_dismissed_${user.uid}`, 'true');
  };

  const handleDeleteAccount = async () => {
    if (window.confirm("Are you sure you want to delete your account? This action is permanent and cannot be undone.")) {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (currentUser) {
          await deleteUser(currentUser);
          navigate('/'); // Redirect to landing page after deletion
        }
      } catch (error) {
        console.error("Error deleting account:", error);
        if (error.code === 'auth/requires-recent-login') {
          alert("For security reasons, please log out and log back in to verify your identity before deleting your account.");
        } else {
          alert("Failed to delete account. Please try again.");
        }
      }
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case 'home':
        return renderDashboard();
      case 'bookings':
        return renderBookings();
      case 'profile':
        return renderProfile();
      default:
        return renderDashboard();
    }
  };

  const renderBookings = () => {
    return (
      <div className="mb-12">
        <h1 className="text-4xl font-light tracking-wide mb-6" style={{ color: BRAND_COLORS.primary }}>
          Work History
        </h1>

        {workHistory.length > 0 ? (
          <div className="grid gap-6">
            {workHistory.map((app) => (
              <div
                key={app.id}
                onClick={() => navigate(`/professional/jobs/${app.jobId}`)}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-medium mb-1">{app.jobDetails.title}</h3>
                    <p className="text-sm text-gray-500">Completed on: {new Date(app.updatedAt?.toDate()).toLocaleDateString()}</p>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                    Completed
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="w-4 h-4 mr-2" />
                    <span className="text-sm">{app.jobDetails.location?.address}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <IndianRupee className="w-4 h-4 mr-2" />
                    <span className="text-sm">₹{app.jobDetails.budget?.value}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-500">No work history available.</p>
          </div>
        )}
      </div>
    );
  };

  const renderDashboard = () => {
    const isVerified = professionalData?.verificationStatus === 'verified';
    const isDeactivated = professionalData?.deactivated || false;

    return (
      <>
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl font-light tracking-wide mb-2" style={{ color: BRAND_COLORS.primary }}>
            Welcome, {professionalData?.displayName || 'Professional'}
          </h1>
          <p className="text-gray-600 font-light">Your SkillVend Dashboard</p>
        </div>

        {/* Notification Prompt */}
        <NotificationPrompt
          userId={user.uid}
          userType="professional"
          message="Get instant alerts about new job opportunities that match your skills and location. Enable notifications to stay ahead and never miss out on the perfect opportunity."
        />

        {/* PWA Install Prompt */}
        <InstallPwaPrompt userId={user.uid} />

        {/* Deactivation Banner */}
        {isDeactivated && (
          <div className="bg-red-50 border-2 border-red-200 p-8 md:p-10 mb-8">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 border-2 border-red-300 rounded-full flex items-center justify-center bg-red-100">
                  <X className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-light tracking-wide mb-4 text-red-800">
                  Account Deactivated
                </h2>
                <p className="text-red-700 font-medium mb-2">
                  Your profile is deactivated and you wont recieve jobs contact admin to activate it
                </p>
                {professionalData?.adminDeactivationReason && (
                  <p className="text-red-600 font-light">
                    <strong>Reason:</strong> {professionalData.adminDeactivationReason}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Verification Status Card */}
        {(!isVerified || showVerifiedBanner) && !isDeactivated && (
          <div className="bg-white border-2 p-8 md:p-10 mb-8 relative" style={{ borderColor: BRAND_COLORS.primary }}>
            {!isVerified ? (
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 border-2 rounded-full flex items-center justify-center" style={{ borderColor: BRAND_COLORS.accent }}>
                    <Clock className="w-8 h-8" style={{ color: BRAND_COLORS.accent }} />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-light tracking-wide mb-4" style={{ color: BRAND_COLORS.primary }}>
                    Verification in Progress
                  </h2>
                  <p className="text-gray-600 font-light leading-relaxed mb-4">
                    Thank you for completing your profile! Our team is currently reviewing and verifying your information.
                    This process typically takes 24-48 hours.
                  </p>
                  <p className="text-gray-600 font-light leading-relaxed mb-4">
                    We will notify you via SMS and in-app notification once your profile has been verified and you can
                    start browsing and applying for job opportunities.
                  </p>
                  <div className="p-4 border-2 bg-gray-50 mt-6" style={{ borderColor: BRAND_COLORS.lightGray }}>
                    <p className="text-sm font-light text-gray-600">
                      <span className="font-normal" style={{ color: BRAND_COLORS.primary }}>What's next?</span>
                      <br />
                      Once verified, you'll be able to:
                    </p>
                    <ul className="mt-3 space-y-2 text-sm font-light text-gray-600 ml-4">
                      <li>• Browse available job opportunities near you</li>
                      <li>• Apply for jobs that match your skills</li>
                      <li>• Manage your applications and bookings</li>
                      <li>• Update your availability status</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <button
                  onClick={handleDismissVerifiedBanner}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Dismiss"
                >
                  <X size={24} />
                </button>
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 border-2 rounded-full flex items-center justify-center" style={{ borderColor: BRAND_COLORS.accent }}>
                      <CheckCircle className="w-8 h-8" style={{ color: BRAND_COLORS.accent }} />
                    </div>
                  </div>
                  <div className="flex-1 pr-8">
                    <h2 className="text-2xl font-light tracking-wide mb-4" style={{ color: BRAND_COLORS.primary }}>
                      Profile Verified!
                    </h2>
                    <p className="text-gray-600 font-light leading-relaxed">
                      Your profile has been successfully verified. You can now browse and apply for job opportunities.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Invited Job Offers Section */}
        {
          invitedJobs.length > 0 && (
            <div className="mb-12 animate-pulse-once">
              <div className="flex items-center mb-6">
                <h2 className="text-2xl font-light tracking-wide mr-3" style={{ color: BRAND_COLORS.primary }}>
                  Job Invitations
                </h2>
                <span className="flex h-3 w-3 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
              </div>

              <div className="grid gap-6">
                {invitedJobs.map((app) => (
                  <div
                    key={app.id}
                    onClick={() => navigate(`/professional/jobs/${app.jobId}`)}
                    className="bg-white border-2 border-yellow-400 rounded-lg p-6 shadow-md cursor-pointer hover:shadow-lg transition-all transform hover:-translate-y-1"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-medium mb-1">{app.jobDetails.title}</h3>
                        <p className="text-sm text-gray-500">Application ID: {app.id}</p>
                      </div>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full flex items-center">
                        <Bell className="w-3 h-3 mr-1" />
                        Invited
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span className="text-sm">{app.jobDetails.location?.address}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span className="text-sm">{app.jobDetails.date} at {app.jobDetails.time}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <IndianRupee className="w-4 h-4 mr-2" />
                        <span className="text-sm">₹{app.jobDetails.budget?.value}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
                      <button className="text-sm font-medium hover:underline" style={{ color: BRAND_COLORS.accent }}>
                        View Details to Accept &rarr;
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        }

        {/* Upcoming Jobs Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-light tracking-wide mb-6" style={{ color: BRAND_COLORS.primary }}>
            Upcoming Jobs
          </h2>
          {upcomingJobs.length > 0 ? (
            <div className="grid gap-6">
              {upcomingJobs.map((app) => (
                <div
                  key={app.id}
                  onClick={() => navigate(`/professional/jobs/${app.jobId}`)}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-medium mb-1">{app.jobDetails.title}</h3>
                      <p className="text-sm text-gray-500">Application ID: {app.id}</p>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                      Accepted
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span className="text-sm">{app.jobDetails.location?.address}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-sm">{app.jobDetails.date} at {app.jobDetails.time}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <IndianRupee className="w-4 h-4 mr-2" />
                      <span className="text-sm">₹{app.jobDetails.budget?.value}</span>
                    </div>
                  </div>

                  {/* Employer Details (Phone Number) */}
                  <div className="border-t pt-4 mt-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Employer Contact</p>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                        <Phone className="w-4 h-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">View details for contact info</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-500">No upcoming jobs yet.</p>
            </div>
          )}
        </div>


      </>
    );
  };

  const renderProfile = () => {
    return (
      <>
        <div className="mb-12">
          <h1 className="text-4xl font-light tracking-wide mb-2" style={{ color: BRAND_COLORS.primary }}>
            Your Profile
          </h1>
          <p className="text-gray-600 font-light">View and manage your professional information</p>
        </div>

        {/* Profile Summary */}
        {professionalData && (
          <div className="bg-white border-2 p-8 md:p-10" style={{ borderColor: BRAND_COLORS.primary }}>
            <h3 className="text-xl font-light tracking-wide mb-6" style={{ color: BRAND_COLORS.primary }}>
              Profile Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-light tracking-widest mb-2 uppercase text-gray-500">Location</p>
                <p className="font-light" style={{ color: BRAND_COLORS.primary }}>
                  {professionalData.address?.city || 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-xs font-light tracking-widest mb-2 uppercase text-gray-500">Skills</p>
                <p className="font-light" style={{ color: BRAND_COLORS.primary }}>
                  {professionalData.skills?.join(', ') || 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-xs font-light tracking-widest mb-2 uppercase text-gray-500">Languages</p>
                <p className="font-light" style={{ color: BRAND_COLORS.primary }}>
                  {professionalData.languages?.join(', ') || 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-xs font-light tracking-widest mb-2 uppercase text-gray-500">Status</p>
                <p className="font-light" style={{ color: BRAND_COLORS.primary }}>
                  {professionalData.isAvailableNow ? 'Available Now' : 'Not Available'}
                </p>
              </div>
            </div>

            {/* Delete Account Button */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100 transition-colors text-sm font-medium"
              >
                Delete Account
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.light }}>
        <p className="text-gray-600 font-light">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: BRAND_COLORS.light }}>
      <Header
        user={user}
        userProfile={userProfile}
        currentView={currentView}
        onViewChange={setCurrentView}
      />
      <div className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalHomePage;
